// Landing-page i18n keys (used by LandingPage.vue).
// Page-level keys live in this file; shared keys (e.g. "common.cancel") live in
// the central i18n.ts dict. Brand name "WineStock" is intentionally kept as-is
// in both languages (it's a product name, not translatable content).

export const landingDict: Record<string, [en: string, th: string]> = {
  // ── Navbar ──────────────────────────────────────────────
  "Landing.nav.features":       ["Features",     "ฟีเจอร์"],
  "Landing.nav.how":            ["How It Works", "วิธีการทำงาน"],
  "Landing.nav.about":          ["About",        "เกี่ยวกับเรา"],
  "Landing.nav.signIn":         ["Sign In",         "เข้าสู่ระบบ"],
  "Landing.nav.goToDashboard":  ["Go to Dashboard", "ไปที่แดชบอร์ด"],
  "Landing.nav.drawerSignIn":   ["Sign In to Dashboard", "เข้าสู่ระบบเพื่อใช้งานแดชบอร์ด"],

  // ── Hero ────────────────────────────────────────────────
  "Landing.hero.eyebrow":         ["RFID-Powered Wine Platform", "แพลตฟอร์มจัดการไวน์ด้วย RFID"],
  "Landing.hero.headline.line1":  ["Track Every",                "ติดตามทุก"],
  "Landing.hero.headline.line2":  ["Bottle.",                    "ขวด"],
  "Landing.hero.headline.line3":  ["In Real Time.",              "แบบเรียลไทม์"],
  "Landing.hero.subtitle":        [
    "Intelligent wine inventory management powered by RFID technology. From stock to glass — complete visibility, total control.",
    "ระบบจัดการสต็อกไวน์อัจฉริยะที่ใช้เทคโนโลยี RFID ตั้งแต่รับเข้าจนถึงเสิร์ฟ — มองเห็นครบ ควบคุมเต็มที่",
  ],
  "Landing.hero.ctaPrimary":    ["Get Started",   "เริ่มต้นใช้งาน"],
  "Landing.hero.ctaSecondary":  ["Explore Features", "สำรวจฟีเจอร์"],

  // ── Hero stats ──────────────────────────────────────────
  "Landing.stat.accuracy":  ["Accuracy",  "ความแม่นยำ"],
  "Landing.stat.scan":      ["Scan",      "การสแกน"],
  "Landing.stat.monitor":   ["Monitoring", "ติดตาม"],
  "Landing.stat.scale":     ["Scalable",  "ขยายระบบได้"],

  // ── Ticker (the scrolling keywords strip) ───────────────
  "Landing.ticker.rfidTracking":    ["RFID Tracking",      "ติดตาม RFID"],
  "Landing.ticker.liveInventory":   ["Live Inventory",     "สต็อกเรียลไทม์"],
  "Landing.ticker.smartAudits":     ["Smart Audits",       "ตรวจนับอัจฉริยะ"],
  "Landing.ticker.wineCatalog":     ["Wine Catalog",       "แคตตาล็อกไวน์"],
  "Landing.ticker.locationMgmt":    ["Location Management", "จัดการตำแหน่ง"],
  "Landing.ticker.activityLogs":    ["Activity Logs",      "บันทึกกิจกรรม"],
  "Landing.ticker.roleAccess":      ["Role Access",        "สิทธิ์ผู้ใช้"],
  "Landing.ticker.receiptManagement":   ["Receipt Management", "จัดการใบรับ"],

  // ── Features section ────────────────────────────────────
  "Landing.features.pill":   ["Features", "ฟีเจอร์"],
  "Landing.features.title":   [
    "Everything you need to manage your stock",
    "ทุกสิ่งที่คุณต้องการเพื่อจัดการสต็อก",
  ],
  "Landing.features.subtitle": [
    "A comprehensive RFID-powered platform for modern wine management.",
    "แพลตฟอร์มที่ใช้ RFID ครบวงจรสำหรับการจัดการไวน์ยุคใหม่",
  ],

  // Feature cards
  "Landing.feature.rfid.title":       ["RFID Tracking", "ติดตาม RFID"],
  "Landing.feature.rfid.desc":        [
    "Assign unique RFID tags to every bottle. Scan, locate, and verify inventory instantly.",
    "กำหนดแท็ก RFID เฉพาะให้ทุกขวด สแกน ค้นหา และตรวจสอบสต็อกได้ทันที",
  ],
  "Landing.feature.catalog.title":    ["Wine Catalog", "แคตตาล็อกไวน์"],
  "Landing.feature.catalog.desc":     [
    "Complete catalog with metadata, vintages, regions, and receipt-level tracking.",
    "แคตตาล็อกครบถ้วนพร้อมข้อมูล ปีผลิต แหล่งผลิต และการติดตามระดับใบรับ",
  ],
  "Landing.feature.audits.title":     ["Smart Audits", "ตรวจนับอัจฉริยะ"],
  "Landing.feature.audits.desc":      [
    "Run RFID audits in minutes. Discrepancy reports and full activity logs.",
    "ตรวจนับด้วย RFID ในไม่กี่นาที พร้อมรายงานส่วนต่างและบันทึกกิจกรรมครบถ้วน",
  ],
  "Landing.feature.locations.title":  ["Locations", "ตำแหน่งจัดเก็บ"],
  "Landing.feature.locations.desc":   [
    "Define zones, racks, shelves. Know where every bottle sits across all sites.",
    "กำหนดโซน แร็ค ชั้นวาง รู้ตำแหน่งของทุกขวดในทุกสาขา",
  ],
  "Landing.feature.access.title":     ["Access Control", "ควบคุมสิทธิ์"],
  "Landing.feature.access.desc":      [
    "Granular role-based permissions. Control who views, edits, or manages what.",
    "สิทธิ์แบบละเอียดตามบทบาท ควบคุมว่าใครดู แก้ไข หรือจัดการอะไรได้บ้าง",
  ],
  "Landing.feature.dashboard.title":  ["Dashboard", "แดชบอร์ด"],
  "Landing.feature.dashboard.desc":   [
    "Visualize everything at a glance. Charts, alerts, and actionable insights.",
    "เห็นทุกอย่างในภาพรวม ทั้งกราฟ การแจ้งเตือน และข้อมูลเชิงลึกที่นำไปใช้ได้",
  ],

  // ── How it works section ────────────────────────────────
  "Landing.how.pill":   ["How It Works", "วิธีการทำงาน"],
  "Landing.how.title":  ["Three steps to total control", "สามขั้นตอนสู่การควบคุมเต็มรูปแบบ"],

  "Landing.how.step1.title": ["Tag Your Bottles", "ติดแท็กขวดของคุณ"],
  "Landing.how.step1.desc": [
    "Attach RFID tags and register each bottle with its wine details, vintage, and storage location.",
    "ติดแท็ก RFID และลงทะเบียนขวดแต่ละขวดพร้อมรายละเอียดไวน์ ปีผลิต และตำแหน่งจัดเก็บ",
  ],
  "Landing.how.step2.title": ["Scan & Track", "สแกนและติดตาม"],
  "Landing.how.step2.desc": [
    "Use RFID readers to scan shelves or individual bottles. Inventory updates in real-time.",
    "ใช้เครื่องอ่าน RFID สแกนชั้นวางหรือขวดแต่ละขวด สต็อกอัปเดตแบบเรียลไทม์",
  ],
  "Landing.how.step3.title": ["Monitor & Report", "ติดตามและรายงาน"],
  "Landing.how.step3.desc": [
    "View live insights on your dashboard, run audits, and generate compliance reports.",
    "ดูข้อมูลเชิงลึกแบบเรียลไทม์บนแดชบอร์ด ตรวจนับ และสร้างรายงานการปฏิบัติตามข้อกำหนด",
  ],

  // ── About section ───────────────────────────────────────
  "Landing.about.pill":   ["About", "เกี่ยวกับเรา"],
  "Landing.about.title": [
    "Built for wine professionals who demand precision",
    "สร้างมาเพื่อผู้เชี่ยวชาญด้านไวน์ที่ต้องการความแม่นยำ",
  ],
  "Landing.about.body": [
    "WineStock combines industry-leading RFID technology with an intuitive management platform. Whether you manage a private collection or a commercial stock, our system provides complete traceability, automated audits, and actionable insights — all in real time.",
    "WineStock ผสานเทคโนโลยี RFID ชั้นนำเข้ากับแพลตฟอร์มจัดการที่ใช้ง่าย ไม่ว่าคุณจะดูแลคอลเลกชันส่วนตัวหรือสต็อกเชิงพาณิชย์ ระบบของเราให้การตรวจสอบย้อนกลับครบถ้วน ตรวจนับอัตโนมัติ และข้อมูลเชิงลึกที่นำไปใช้ได้จริง — ทั้งหมดแบบเรียลไทม์",
  ],

  "Landing.about.badge.security":   ["Enterprise-grade security",  "ความปลอดภัยระดับองค์กร"],
  "Landing.about.badge.uptime":     ["99.9% uptime guarantee",    "รับประกัน uptime 99.9%"],
  "Landing.about.badge.multiSite":  ["Multi-location support",     "รองรับหลายสาขา"],
  "Landing.about.badge.support":    ["Dedicated support team",     "ทีมซัพพอร์ตเฉพาะทาง"],
  "Landing.about.badge.compliance": ["Automated compliance",       "การปฏิบัติตามข้อกำหนดอัตโนมัติ"],
  "Landing.about.badge.auditTrail": ["Full audit trail",           "บันทึกการตรวจสอบย้อนกลับครบถ้วน"],

  // ── CTA section ─────────────────────────────────────────
  "Landing.cta.title":    ["Ready to get started?", "พร้อมเริ่มต้นหรือยัง?"],
  "Landing.cta.subtitle": [
    "Sign in and start tracking your wine inventory with RFID precision.",
    "เข้าสู่ระบบและเริ่มติดตามสต็อกไวน์ของคุณด้วยความแม่นยำระดับ RFID",
  ],

  // ── Footer ──────────────────────────────────────────────
  "Landing.footer.brandTagline": [
    "RFID-powered wine inventory management. Track, audit, and manage your collection with precision.",
    "จัดการสต็อกไวน์ด้วย RFID ติดตาม ตรวจนับ และดูแลคอลเลกชันของคุณด้วยความแม่นยำ",
  ],
  "Landing.footer.colPlatform":  ["Platform",  "แพลตฟอร์ม"],
  "Landing.footer.colResources": ["Resources", "ทรัพยากร"],
  "Landing.footer.colLegal":     ["Legal",     "กฎหมาย"],
  "Landing.footer.link.rfid":     ["RFID Tracking", "ติดตาม RFID"],
  "Landing.footer.link.catalog": ["Wine Catalog",  "แคตตาล็อกไวน์"],
  "Landing.footer.link.audits":   ["Smart Audits",  "ตรวจนับอัจฉริยะ"],
  "Landing.footer.link.dashboard":["Dashboard",     "แดชบอร์ด"],
  "Landing.footer.link.how":      ["How It Works",  "วิธีการทำงาน"],
  "Landing.footer.link.about":   ["About",         "เกี่ยวกับเรา"],
  "Landing.footer.link.privacy":  ["Privacy Policy & Terms", "นโยบายความเป็นส่วนตัวและข้อกำหนด"],
  "Landing.footer.copyrightShort": [
    "All rights reserved.",
    "สงวนลิขสิทธิ์ทั้งหมด",
  ],
  "Landing.footer.legalLink": ["Legal", "กฎหมาย"],

  // ── Language switcher ───────────────────────────────────
  "Landing.lang.english":  ["English",     "ภาษาอังกฤษ"],
  "Landing.lang.thai":     ["Thai",        "ภาษาไทย"],
  "Landing.lang.label":    ["Language",    "ภาษา"],
};
